import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";

type TabKey = "movies" | "news";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState<TabKey>("movies");

  useEffect(() => {
    setInput(q);
    setTab("movies");
  }, [q]);

  useSeo({
    title: q ? `"${q}" 的搜索结果 - 一本道影视大全` : "搜索 - 一本道影视大全",
    description: `在一本道影视大全搜索"${q}"相关的影视作品与资讯内容，快速找到你感兴趣的影片与行业动态。`,
    keywords: `${q},影视搜索,一本道搜索,影视作品,影视资讯`,
  });

  const matchedMovies = useMemo(() => {
    if (!q) return [];
    const key = q.toLowerCase();
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(key) ||
        m.genres.some((g) => g.includes(q)) ||
        m.region.includes(q) ||
        m.actors.some((a) => a.includes(q)) ||
        m.director.includes(q) ||
        m.tags.some((t) => t.includes(q))
    );
  }, [q]);

  const matchedNews = useMemo(() => {
    if (!q) return [];
    const key = q.toLowerCase();
    return news.filter(
      (n) => n.title.toLowerCase().includes(key) || n.summary.includes(q) || n.tags.some((t) => t.includes(q))
    );
  }, [q]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    setSearchParams({ q: value });
  };

  const hotMovies = [...movies].sort((a, b) => b.views - a.views).slice(0, 4);
  const hotNews = news.slice(0, 3);
  const totalResults = matchedMovies.length + matchedNews.length;

  const tabs = [
    { key: "movies" as TabKey, label: "影视作品", count: matchedMovies.length },
    { key: "news" as TabKey, label: "影视资讯", count: matchedNews.length },
  ];

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">搜索结果</span>
          </nav>
          <form onSubmit={handleSubmit} className="max-w-2xl">
            <div className="relative w-full">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索影视作品、资讯..."
                className="w-full h-12 md:h-14 pl-12 pr-28 rounded-lg bg-background-50 border border-background-200 text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </div>
          </form>
          {q && (
            <p className="mt-4 text-sm text-foreground-600">
              关键词 <span className="font-bold text-primary-600">“{q}”</span> 共找到{" "}
              <span className="font-bold text-accent-600">{totalResults}</span> 条结果
            </p>
          )}
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        {!q ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-lg border border-dashed border-background-300">
            <span className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">输入关键词开始搜索</h3>
            <p className="mt-2 text-sm text-foreground-500">可以搜索影视片名、类型、演员，也可以搜索影视资讯</p>
          </div>
        ) : totalResults === 0 ? (
          <div>
            <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-dashed border-background-300 bg-background-50">
              <span className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">没有找到与“{q}”相关的内容</h3>
              <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐</p>
            </div>

            <div className="mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 mb-5">热门影视推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {hotMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>

            <div className="mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 mb-5">热门资讯</h2>
              <div className="space-y-3">
                {hotNews.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/detail/${item.id}`}
                    className="group flex items-center gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center shrink-0">
                      <i className="ri-newspaper-line"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm md:text-base font-bold text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-400">{item.publishedAt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit mb-8">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    tab === t.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {t.label}（{t.count}）
                </button>
              ))}
            </div>

            {tab === "movies" ? (
              matchedMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {matchedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 rounded-lg border border-dashed border-background-300">
                  <i className="ri-film-line text-4xl text-foreground-300"></i>
                  <p className="mt-4 text-sm text-foreground-500">该分类下没有匹配的影视作品</p>
                </div>
              )
            ) : matchedNews.length > 0 ? (
              <div className="space-y-4">
                {matchedNews.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/detail/${item.id}`}
                    className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-full sm:w-44 h-36 sm:h-24 rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 一本道影视大全`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span className="px-2.5 py-1 rounded bg-secondary-100 text-secondary-800 font-medium">
                          {item.category}
                        </span>
                        <span>{item.publishedAt}</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 rounded-lg border border-dashed border-background-300">
                <i className="ri-newspaper-line text-4xl text-foreground-300"></i>
                <p className="mt-4 text-sm text-foreground-500">该分类下没有匹配的资讯内容</p>
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}